import { 
  LoginCredentials, 
  AuthResponse, 
  RefreshResponse,
  ApiResponse,
  AuthUser
} from '../types'
import { apiClient } from './api'

export interface AttestationData {
  first_name: string
  last_name: string
  email: string
  is_attested: boolean
}

/**
 * True when `token` is a JWT whose `exp` claim is in the past — or when the
 * token cannot be decoded at all. Fail closed: a malformed or unparseable
 * token counts as expired, so `isAuthenticated()` can never answer true on
 * garbage.
 *
 * This is a UX check, not a security boundary: the payload is decoded without
 * verifying the signature, and the server stays the authority on every
 * request. The ONLY claim this reads is `exp` — never trust anything else in
 * an unverified token. The decode is ~5 lines of base64url, so no JWT library
 * is warranted for it.
 *
 * Why it exists: tokens live 24h. `isAuthenticated()` used to check only that
 * a token EXISTED in localStorage, so a user holding a stale token still read
 * as signed in — `AuthRoute` bounced them away from /login, every API call
 * failed, and they were trapped until they cleared localStorage by hand.
 */
export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return true

    // JWT payloads are base64url (RFC 7515): '-' and '_' instead of '+' and
    // '/', usually without '=' padding — atob wants standard base64.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const payload: unknown = JSON.parse(atob(padded))

    const exp =
      payload && typeof payload === 'object'
        ? (payload as { exp?: unknown }).exp
        : undefined
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return true

    // `exp` is seconds since epoch (RFC 7519), Date.now() is milliseconds.
    return exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

class AuthService {
  private token: string | null = null
  private user: AuthUser | null = null
  private api = apiClient

  constructor() {
    this.loadFromStorage()
  }

  private loadFromStorage() {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      this.token = storedToken
      this.user = JSON.parse(storedUser)
    }
  }

  private saveToStorage(token: string, user: AuthUser) {
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(user))
    this.token = token
    this.user = user
  }

  private clearStorage() {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    // 🔴 The signup breadcrumbs belong to the SESSION, not to the machine.
    //
    // `institutional_signup_draft` is a pre-organization mirror of the
    // institutional profile — tax ID / EIN, incorporation facts, contact
    // details, mailing address (banking is deliberately excluded from it). It
    // survived logout, so the next person to start institutional signup on a
    // shared machine resumed into the previous user's entity. And a stale
    // `signup_account_type` routes a fresh user down the wrong branch of the
    // funnel before the server has said anything.
    localStorage.removeItem('institutional_signup_draft')
    localStorage.removeItem('signup_account_type')
    this.token = null
    this.user = null
  }

  getToken(): string | null {
    return this.token
  }

  getUser(): AuthUser | null {
    return this.user
  }

  isAuthenticated(): boolean {
    // Presence is not enough: an expired (or undecodable) token must read as
    // signed OUT, or the route guards trap the user away from /login.
    return !!this.token && !!this.user && !isTokenExpired(this.token)
  }

  async login(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      const response = await this.api.post<AuthResponse>('/user/auth', credentials)
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'No data received from login endpoint')
      }

      const { token, user } = response.data
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  async signup(userData: Partial<AuthUser>): Promise<AuthUser> {
    try {
      const response = await this.api.post<AuthResponse>('/user/signup', userData)
      console.log('Signup API Response:', response.data)
      
      if (!response.data) {
        throw new Error('No data received from signup endpoint')
      }
      
      const { token, user } = response.data
      
      // For signup, we need to login after successful registration
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('Signup failed:', error)
      throw error
    }
  }

  async refreshToken(): Promise<void> {
    try {
      const response = await this.api.post<RefreshResponse>('/user/refresh')
      
      if (!response.success || !response.data) {
        throw new Error('No data received from refresh token endpoint')
      }
      
      const { token, user } = response.data
      
      // Update only the token, keep the rest of the user data
      if (this.user) {
        this.saveToStorage(token, { ...this.user, ...user })
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      this.logout()
      throw error
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      const changePasswordData = {
        old_password: oldPassword,
        new_password: newPassword
      }
      
      const response = await this.api.post<ApiResponse<{ message: string }>>('/user/change-password', changePasswordData)
      
      if (!response.success || !response.data) {
        throw new Error('No data received from change password endpoint')
      }
    } catch (error) {
      console.error('Password change failed:', error)
      throw error
    }
  }

  async updateProfile(updates: Partial<AuthUser>): Promise<AuthUser> {
    try {
      const response = await this.api.put<AuthResponse>('/user/profile', updates)
      
      if (!response.success || !response.data) {
        throw new Error('No data received from update profile endpoint')
      }
      
      const { token, user } = response.data
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('Profile update failed:', error)
      throw error
    }
  }

  async validateSession(): Promise<boolean> {
    try {
      if (!this.token) {
        return false
      }

      // Use /user/refresh to check session
      const response = await this.api.post<RefreshResponse>('/user/refresh')
      
      if (response.success && response.data) {
        const { token, user } = response.data
        this.saveToStorage(token, { ...this.user, ...user })
        return true
      }
      
      return false
    } catch (error) {
      console.error('Session validation failed:', error)
      this.clearStorage() // Clear storage on error
      return false
    }
  }

  async requestOtpCode(email: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse<{ message: string }>>('/user/otp-login/request', { email })
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to send OTP code')
      }
    } catch (error) {
      console.error('OTP request failed:', error)
      throw error
    }
  }

  async confirmOtpCode(email: string, otp: string): Promise<AuthUser> {
    try {
      const response = await this.api.post<AuthResponse>('/user/otp-login/confirm', { email, otp })
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Invalid OTP code')
      }

      const { token, user } = response.data
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('OTP confirmation failed:', error)
      throw error
    }
  }

  logout() {
    this.clearStorage()
  }
}

export const authService = new AuthService() 